import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@iventis/styles";
import { Body2 } from "@iventis/styles/src/components/texts";
import type { ChangeLogEntry } from "@iventis/domain-model/model/changeLogEntry";
import type { PagedCursorResult } from "@iventis/domain-model/model/pagedCursorResult";
import { Virtuoso } from "react-virtuoso";
import { useIventisTranslate, UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { ChangeLogSectionSkeletonComponent } from "./change-log-section-skeleton";
import { ChangeLogItemComponent } from "./change-log-item";
import { ChangeLogItemSkeltonComponent } from "./change-log-item-skeleton";
import type { changeLogItem } from "./change-log-item-types";
import { getDateHeader } from "./change-log-helpers";

export type VirtualScrollingChangeLogItem = changeLogItem & { id: string };

type ChangeLogVirtualRow = { type: "header"; id: string; title: string } | { type: "item"; id: string; item: VirtualScrollingChangeLogItem };

export type VirtualScrollingChangeLogProps = {
    requester: (cursorId?: string) => Promise<PagedCursorResult<ChangeLogEntry[]>>;
    mapper: (entries: ChangeLogEntry[], translate: UseIventisTranslate) => VirtualScrollingChangeLogItem[];
    refreshTrigger?: number;
};

export const VirtualScrollingChangeLogComponent: FunctionComponent<VirtualScrollingChangeLogProps> = ({ requester, mapper, refreshTrigger }) => {
    const translate = useIventisTranslate();
    const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [nextCursorId, setNextCursorId] = useState<string | undefined>(undefined);
    const [lastPage, setLastPage] = useState<boolean>(true);

    const items = useMemo(() => mapper(entries, translate), [entries, mapper]);
    const rows = useMemo<ChangeLogVirtualRow[]>(() => {
        const builtRows: ChangeLogVirtualRow[] = [];
        let previousHeader: string | undefined;

        for (const item of items) {
            const header = getDateHeader(item.time);
            if (header !== previousHeader) {
                builtRows.push({
                    type: "header",
                    id: `header-${header}`,
                    title: header,
                });
                previousHeader = header;
            }

            builtRows.push({
                type: "item",
                id: item.id,
                item,
            });
        }

        return builtRows;
    }, [items]);

    useEffect(() => {
        let cancelled = false;

        const fetchInitialPage = async () => {
            setLoading(true);
            try {
                const page = await requester();
                if (cancelled) return;

                setEntries(
                    page.results.map((entry) => ({
                        ...entry,
                        changedAt: new Date(entry.changedAt),
                    }))
                );
                setNextCursorId(page.nextCursorId);
                setLastPage(page.lastPage);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchInitialPage();

        return () => {
            cancelled = true;
        };
    }, [requester, refreshTrigger]);

    const loadMore = useCallback(async () => {
        if (loading || loadingMore || lastPage || !nextCursorId) {
            return;
        }

        setLoadingMore(true);
        try {
            const page = await requester(nextCursorId);
            setEntries((currentEntries) => {
                const existingIds = new Set(currentEntries.map((entry) => entry.id));
                const newEntries = page.results
                    .map((entry) => ({
                        ...entry,
                        changedAt: new Date(entry.changedAt),
                    }))
                    .filter((entry) => !existingIds.has(entry.id));
                return [...currentEntries, ...newEntries];
            });

            setNextCursorId(page.nextCursorId);
            setLastPage(page.lastPage);
        } finally {
            setLoadingMore(false);
        }
    }, [lastPage, loading, loadingMore, nextCursorId, requester]);

    if (loading) {
        return (
            <Wrapper>
                <ChangeLogSectionSkeletonComponent />
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Section>
                <Virtuoso
                    style={{ height: "100%" }}
                    data={rows}
                    endReached={loadMore}
                    itemContent={(index, row) => {
                        if (row.type === "header") {
                            return (
                                <RowHeader>
                                    <RowHeaderDivider />
                                    <Body2>{row.title}</Body2>
                                    <RowHeaderDivider />
                                </RowHeader>
                            );
                        }

                        const previousRow = index > 0 ? rows[index - 1] : undefined;
                        const nextRow = index < rows.length - 1 ? rows[index + 1] : undefined;
                        const isGroupStart = previousRow?.type !== "item";
                        const isGroupEnd = nextRow?.type !== "item";

                        return (
                            <ItemRowContainer data-testid="change-log-list-item" isGroupStart={isGroupStart} isGroupEnd={isGroupEnd}>
                                <PaddedChangeLogItem changeLogItem={row.item} />
                            </ItemRowContainer>
                        );
                    }}
                    components={{
                        Footer: () =>
                            loadingMore ? (
                                <ItemRowContainer isGroupStart isGroupEnd>
                                    <PaddedChangeLogSkeletonItem />
                                </ItemRowContainer>
                            ) : null,
                    }}
                />
            </Section>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    padding: 5px;
    height: 100%;
    overflow-x: hidden;
`;

const RowHeader = styled.div`
    margin: 8px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const RowHeaderDivider = styled.div`
    flex: 1;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
    margin: 0 8px;
`;

const Section = styled.div`
    height: 100%;
    overflow: hidden;
`;

const ItemRowContainer = styled.div<{ isGroupStart: boolean; isGroupEnd: boolean }>`
    border-left: 1px solid rgba(0, 0, 0, 0.2);
    border-right: 1px solid rgba(0, 0, 0, 0.2);
    border-top: ${({ isGroupStart }) => (isGroupStart ? "1px solid rgba(0, 0, 0, 0.2)" : "none")};
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    border-top-left-radius: ${({ isGroupStart }) => (isGroupStart ? "6px" : "0")};
    border-top-right-radius: ${({ isGroupStart }) => (isGroupStart ? "6px" : "0")};
    border-bottom-left-radius: ${({ isGroupEnd }) => (isGroupEnd ? "6px" : "0")};
    border-bottom-right-radius: ${({ isGroupEnd }) => (isGroupEnd ? "6px" : "0")};
    margin-bottom: ${({ isGroupEnd }) => (isGroupEnd ? "8px" : "0")};
`;

const PaddedChangeLogItem = styled(ChangeLogItemComponent)`
    margin: 0px 12px;
    padding: 8px 0px;
`;

const PaddedChangeLogSkeletonItem = styled(ChangeLogItemSkeltonComponent)`
    margin: 0px 12px;
    padding: 8px 0px;
`;
